import { useState, useEffect, useRef, type FormEvent } from 'react';
import { authApi, ApiError } from '../api/client';
import { useAuthStore } from '../stores/auth.store';

export function TwoFactorPage() {
  const state = useAuthStore((s) => s.state);
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated);
  const setUnauthenticated = useAuthStore((s) => s.setUnauthenticated);

  if (state.status !== 'challenge') {
    return null;
  }

  return <TwoFactorForm challengeToken={state.challengeToken} expiresAt={state.expiresAt} />;

  function TwoFactorForm({ challengeToken, expiresAt }: { challengeToken: string; expiresAt: number }) {
    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(() => Math.max(0, Math.floor((expiresAt - Date.now()) / 1000)));
    const inputRef = useRef<HTMLInputElement>(null);
    // Track which code we've already submitted, so re-edits after a failed
    // attempt don't fire again before the user has typed a new full 6 digits.
    const lastSubmittedCode = useRef<string | null>(null);

    useEffect(() => {
      inputRef.current?.focus();
    }, []);

    useEffect(() => {
      const id = setInterval(() => {
        const left = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
        setSecondsLeft(left);
        if (left === 0) {
          setUnauthenticated('Code expired — please log in again.');
          clearInterval(id);
        }
      }, 1000);
      return () => clearInterval(id);
    }, [expiresAt]);

    async function verify() {
      if (submitting) return;
      if (lastSubmittedCode.current === code) return;
      lastSubmittedCode.current = code;
      setSubmitting(true);
      setError(null);
      try {
        await authApi.verifyTwoFactor(code, challengeToken);
        const me = await authApi.me();
        setAuthenticated(me);
      } catch (err) {
        const message = err instanceof ApiError ? err.body.message ?? err.body.error ?? 'Invalid code' : 'Invalid code';
        setError(message);
      } finally {
        setSubmitting(false);
      }
    }

    // Auto-submit as soon as the full 6 digits are typed/pasted. Skipped if
    // the same code was already attempted (so a failed code doesn't keep
    // re-firing — the user has to edit a digit to retry).
    useEffect(() => {
      if (code.length === 6 && lastSubmittedCode.current !== code && !submitting) {
        void verify();
      }
    }, [code, submitting]);

    async function onSubmit(e: FormEvent) {
      e.preventDefault();
      await verify();
    }

    return (
      <main className="min-h-screen flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6 bg-surface-2 rounded-2xl p-8 shadow-2xl">
          <div className="text-center">
            <h1 className="font-heading text-xl font-semibold tracking-tight">Verification code</h1>
            <p className="mt-1 text-sm text-ink-muted">
              Check your email for a 6-digit code.
            </p>
          </div>

          <div>
            <label htmlFor="code" className="sr-only">
              6-digit code
            </label>
            <input
              ref={inputRef}
              id="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              maxLength={6}
              pattern="\d{6}"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className="w-full text-center font-mono tracking-[0.4em] text-2xl rounded-lg bg-surface-3 px-3.5 py-3 text-ink outline-none ring-1 ring-transparent focus:ring-primary"
              placeholder="••••••"
            />
          </div>

          {error && (
            <p role="alert" className="text-sm text-bad text-center">
              {error}
            </p>
          )}

          <p className="text-center text-xs text-ink-muted">
            Code expires in <span className="font-mono">{formatSeconds(secondsLeft)}</span>
          </p>

          <button
            type="submit"
            disabled={submitting || code.length !== 6}
            className="w-full rounded-lg bg-primary px-4 py-2.5 font-medium text-ink transition disabled:opacity-50"
          >
            {submitting ? 'Verifying…' : 'Verify'}
          </button>

          <button
            type="button"
            onClick={() => setUnauthenticated()}
            className="w-full text-xs text-ink-muted hover:text-ink"
          >
            Cancel — back to sign in
          </button>
        </form>
      </main>
    );
  }
}

function formatSeconds(s: number): string {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}
